import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, CurrencyEur, Calendar, FileText, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";

const INTERVALS = [
  { value: "monthly", label: "Monatlich", monthsPerYear: 12, divisor: 1 },
  { value: "quarterly", label: "Quartalsweise", monthsPerYear: 4, divisor: 3 },
  { value: "half_year", label: "Halbjährlich", monthsPerYear: 2, divisor: 6 },
  { value: "yearly", label: "Jährlich", monthsPerYear: 1, divisor: 12 },
  { value: "once", label: "Einmalig", monthsPerYear: 1, divisor: 0 },
];

const fmtEUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "—");

export default function CostPositions() {
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState({ predefined: [], custom: [] });
  const [propertyId, setPropertyId] = useState("all");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (propertyId !== "all") params.property_id = propertyId;
    const { data } = await api.get("/cost-positions", { params });
    setItems(data);
  };

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/contacts", { params: { limit: 1000 } }).then((r) => setContacts(r.data));
    api.get("/documents").then((r) => setDocuments(r.data));
    api.get("/cost-positions/categories").then((r) => setCategories(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
  const docMap = useMemo(() => Object.fromEntries(documents.map((d) => [d.id, d])), [documents]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((p) =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.category || "").toLowerCase().includes(s) ||
      (p.payment_holder_text || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const totalMonthly = filtered.reduce((sum, p) => sum + (p.is_active ? Number(p.monthly_average || 0) : 0), 0);

  const { sorted, SortableTh } = useTableSort(filtered, "next_due_date", "asc", {
    name: (p) => p.name || "",
    category: (p) => p.category || "",
    property: (p) => propMap[p.property_id]?.name || "",
    interval: (p) => p.interval || "",
    amount: (p) => Number(p.amount) || 0,
    monthly: (p) => Number(p.monthly_average) || 0,
    next_due_date: (p) => p.next_due_date || "",
  });

  const remove = async (id) => {
    if (!await window._domivusConfirm("Kostenposition wirklich löschen?")) return;
    await api.delete(`/cost-positions/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div className="space-y-4" data-testid="cost-positions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Kosten & Abschläge</h1>
          <p className="text-sm text-slate-500 mt-1">Laufende Kosten je Objekt mit automatischer Monatsumrechnung. Erscheinen im Eigentümerportal.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="add-cost-btn" onClick={() => setEditing(null)}>
              <Plus size={16} /> Neue Kostenposition
            </Button>
          </DialogTrigger>
          <CostDialog
            editing={editing}
            properties={properties}
            contacts={contacts}
            documents={documents}
            categories={categories}
            onSaved={() => { setOpen(false); setEditing(null); load(); api.get("/cost-positions/categories").then((r) => setCategories(r.data)); }}
          />
        </Dialog>
      </div>

      {/* Übersichts-Karten */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard icon={CurrencyEur} label="Monatliche Belastung gesamt" value={fmtEUR(totalMonthly)} accent="indigo" />
        <KpiCard icon={Calendar} label="Aktive Positionen" value={`${filtered.filter((p) => p.is_active).length} / ${filtered.length}`} accent="emerald" />
        <KpiCard icon={FileText} label="Mit verknüpftem Dokument" value={filtered.filter((p) => p.linked_document_id).length} accent="amber" />
      </div>

      {/* Filterleiste */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Suchen nach Name, Kategorie, Empfänger…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="cost-search" />
        </div>
        <Label className="text-xs text-slate-600">Objekt:</Label>
        <Select value={propertyId} onValueChange={setPropertyId}>
          <SelectTrigger className="w-64 h-9" data-testid="cost-property-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Objekte</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.property_number ? `${p.property_number} · ` : ""}{p.name || p.address?.street}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <SortableTh field="name">Bezeichnung</SortableTh>
              <SortableTh field="category">Kategorie</SortableTh>
              <SortableTh field="property">Objekt</SortableTh>
              <SortableTh field="interval">Intervall</SortableTh>
              <SortableTh field="next_due_date">Nächste Fälligkeit</SortableTh>
              <SortableTh field="amount" align="right" className="text-right">Betrag</SortableTh>
              <SortableTh field="monthly" align="right" className="text-right">Pro Monat</SortableTh>
              <Th className="text-right w-24"></Th>
            </tr>
          </thead>
          <tbody data-testid="cost-tbody">
            {sorted.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                <CurrencyEur size={28} className="mx-auto mb-2 text-slate-300" />
                Noch keine Kostenpositionen erfasst.
              </td></tr>
            )}
            {sorted.map((p) => {
              const intervalLabel = INTERVALS.find((i) => i.value === p.interval)?.label || p.interval;
              return (
                <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${!p.is_active ? "opacity-50" : ""}`} data-testid={`cost-row-${p.id}`}>
                  <Td className="font-medium text-slate-900">
                    <div>{p.name}</div>
                    {p.payment_holder_contact_id && <div className="text-[11px] text-slate-500">An: {contacts.find((c) => c.id === p.payment_holder_contact_id)?.company_name || contacts.find((c) => c.id === p.payment_holder_contact_id)?.last_name || "—"}</div>}
                    {!p.payment_holder_contact_id && p.payment_holder_text && <div className="text-[11px] text-slate-500">An: {p.payment_holder_text}</div>}
                    {p.linked_document_id && docMap[p.linked_document_id] && (
                      <div className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                        <FileText size={11} /> {docMap[p.linked_document_id].title}
                      </div>
                    )}
                  </Td>
                  <Td><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{p.category}</span></Td>
                  <Td className="text-xs text-slate-600">{propMap[p.property_id]?.name || "—"}</Td>
                  <Td className="text-xs">{intervalLabel}</Td>
                  <Td className="text-xs tabular-nums">{fmtDate(p.next_due_date)}</Td>
                  <Td className="text-right tabular-nums">{fmtEUR(p.amount)}</Td>
                  <Td className="text-right tabular-nums font-semibold text-slate-900">{p.interval === "once" ? "—" : fmtEUR(p.monthly_average)}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`cost-edit-${p.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" onClick={() => remove(p.id)} data-testid={`cost-del-${p.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
          {sorted.length > 0 && (
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td colSpan={6} className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Monatliche Belastung gesamt:</td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900" data-testid="cost-monthly-total">{fmtEUR(totalMonthly)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent = "slate" }) {
  const c = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-md ${c} flex items-center justify-center`}><Icon size={20} /></div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}

function CostDialog({ editing, properties, contacts, documents, categories, onSaved }) {
  const blank = {
    property_id: properties[0]?.id || "",
    name: "", category: "Grundsteuer", amount: 0, interval: "monthly",
    next_due_date: "", payment_holder_contact_id: "", payment_holder_text: "",
    iban: "", bic: "", mandate_reference: "", linked_document_id: "", notes: "", is_active: true,
  };
  const [form, setForm] = useState(() => editing ? { ...editing } : blank);
  const [customCategory, setCustomCategory] = useState("");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => { setForm(editing ? { ...editing } : blank); /* eslint-disable-next-line */ }, [editing]);

  const allCategories = [...(categories.predefined || []), ...(categories.custom || [])];
  const docsForProperty = documents.filter((d) => !form.property_id || d.linked_property_id === form.property_id || !d.linked_property_id);

  const submit = async (e) => {
    e.preventDefault();
    const finalCategory = customCategory.trim() || form.category;
    if (!form.property_id) { toast.error("Bitte Objekt wählen"); return; }
    if (!form.name.trim()) { toast.error("Bezeichnung fehlt"); return; }
    if (form.amount <= 0) { toast.error("Betrag muss > 0 sein"); return; }
    const payload = {
      ...form,
      category: finalCategory,
      amount: Number(form.amount),
      next_due_date: form.next_due_date || null,
      payment_holder_contact_id: form.payment_holder_contact_id || null,
      payment_holder_text: form.payment_holder_text || null,
      linked_document_id: form.linked_document_id || null,
    };
    try {
      if (editing) {
        await api.patch(`/cost-positions/${editing.id}`, payload);
        toast.success("Kostenposition aktualisiert");
      } else {
        await api.post("/cost-positions", payload);
        toast.success("Kostenposition angelegt");
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  const intervalInfo = INTERVALS.find((i) => i.value === form.interval);
  const monthlyPreview = intervalInfo?.divisor ? (Number(form.amount) || 0) / intervalInfo.divisor : 0;

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cost-dialog">
      <DialogHeader>
        <DialogTitle>{editing ? "Kostenposition bearbeiten" : "Neue Kostenposition"}</DialogTitle>
        <DialogDescription>Eine laufende oder einmalige Kostenposition für ein Objekt.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <F label="Objekt">
          <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
            <SelectTrigger data-testid="cost-property"><SelectValue placeholder="Objekt wählen…" /></SelectTrigger>
            <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.property_number ? `${p.property_number} · ` : ""}{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Bezeichnung"><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="z. B. Grundsteuer 2026" data-testid="cost-name" /></F>

        <div className="grid grid-cols-2 gap-3">
          <F label="Kategorie">
            <Select value={form.category} onValueChange={(v) => { set("category", v); setCustomCategory(""); }}>
              <SelectTrigger data-testid="cost-category"><SelectValue /></SelectTrigger>
              <SelectContent>{allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="mt-1" placeholder="Eigene Kategorie hinzufügen…" value={customCategory} onChange={(e) => setCustomCategory(e.target.value)} data-testid="cost-custom-cat" />
          </F>
          <F label="Intervall">
            <Select value={form.interval} onValueChange={(v) => set("interval", v)}>
              <SelectTrigger data-testid="cost-interval"><SelectValue /></SelectTrigger>
              <SelectContent>{INTERVALS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Betrag (€)">
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="cost-amount" />
          </F>
          <F label="Nächste Fälligkeit">
            <Input type="date" value={form.next_due_date || ""} onChange={(e) => set("next_due_date", e.target.value)} data-testid="cost-next-due" />
          </F>
        </div>

        {form.interval !== "once" && form.interval !== "monthly" && Number(form.amount) > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2.5 text-xs text-indigo-800">
            <strong>Umgerechnet pro Monat:</strong> {fmtEUR(monthlyPreview)}
            <span className="text-indigo-600"> (bei {intervalInfo?.label.toLowerCase()})</span>
          </div>
        )}

        <F label="Zahlungsempfänger – aus Kontakten">
          <Select value={form.payment_holder_contact_id || "none"} onValueChange={(v) => set("payment_holder_contact_id", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="cost-holder-contact"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Kein Kontakt / Freitext nutzen —</SelectItem>
              {contacts.map((c) => <SelectItem key={c.id} value={c.id}>
                {c.contact_type === "company" || c.contact_type === "institution"
                  ? c.company_name
                  : `${c.first_name || ""} ${c.last_name || ""}`.trim()}
              </SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        {!form.payment_holder_contact_id && (
          <F label="Zahlungsempfänger – Freitext">
            <Input value={form.payment_holder_text || ""} onChange={(e) => set("payment_holder_text", e.target.value)} placeholder="z. B. Stadtwerke Bremen" data-testid="cost-holder-text" />
          </F>
        )}

        <F label="Dokument verknüpfen (Bescheid, Rechnung)">
          <Select value={form.linked_document_id || "none"} onValueChange={(v) => set("linked_document_id", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="cost-linked-doc"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Kein Dokument —</SelectItem>
              {docsForProperty.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>

        <F label="Notizen (optional)">
          <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="cost-notes" />
        </F>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="cost-save">
            {editing ? "Aktualisieren" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}
